// @utils/effects/retryWithDelay.ts

import { Duration, Effect } from "effect";
import { retryDelayMs } from "@ps-bridge/models/core/retryPolicy";
import type { EffectTs } from "@ps-bridge/interop";
import { effectFromRaw, toRawEffect } from '@utils/effects/branded';
import { intRound } from "@src/purescript/interop";

/**
 * Retry an Effect once after a delay if the error matches a predicate.
 *
 * @param effect The original Effect
 * @param delayMs How long to wait before retrying
 * @param shouldRetry A predicate to decide whether to retry
 */
export const retryWithDelay = <E, A>(
    effect: EffectTs<A, E>,
    delayMs: number,
    shouldRetry: (e: E) => boolean,
): EffectTs<A, E> =>
    effectFromRaw(
        Effect.catchAll(toRawEffect(effect), (error) =>
            shouldRetry(error)
                ? Effect.flatMap(Effect.sleep(Duration.millis(delayMs)), () => toRawEffect(effect))
                : Effect.fail(error),
        ),
    );

export const retryWithBackoff = <E, A>(
    effect: EffectTs<A, E>,
    attempt: number,
    shouldRetry: (e: E) => boolean,
): EffectTs<A, E> =>
    retryWithDelay(effect, retryDelayMs(intRound(attempt)), shouldRetry);
